import { notification } from "antd";
import type { NotificationArgsProps } from "antd";

import { ApiError } from "../ApiError";
import {
  CIQError,
  CompleteValidatorErrors,
  ErrorResponse
} from "../models/api";

type NotificationType = "success" | "error" | "warning" | "info";

type Position =
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right"
  | "top-center"
  | "bottom-center";

const DEFAULT_DURATION = 4;
const DEFAULT_PLACEMENT = "bottomRight";

const positionMap: Record<Position, NotificationArgsProps["placement"]> = {
  "top-left": "topLeft",
  "top-right": "topRight",
  "bottom-left": "bottomLeft",
  "bottom-right": "bottomRight",
  "top-center": "top",
  "bottom-center": "bottom"
};

export class ToasterService {
  private readonly showNotification = (
    type: NotificationType,
    message: string,
    isFixed: boolean,
    position?: Position,
    btn?: React.ReactNode,
    description?: React.ReactNode
  ): void => {
    notification.destroy();
    notification[type]({
      message,
      duration: isFixed ? 0 : DEFAULT_DURATION,
      placement: position ? positionMap[position] : DEFAULT_PLACEMENT,
      btn,
      description
    });
  };

  public showSuccess = (
    message: string,
    position?: Position,
    btn?: React.ReactNode,
    description?: React.ReactNode
  ): void => {
    this.showNotification(
      "success",
      message,
      false,
      position,
      btn,
      description
    );
  };

  public showSuccessFixed = (message: string, position?: Position): void => {
    this.showNotification("success", message, true, position);
  };

  public showError = (
    message: string,
    position?: Position,
    btn?: React.ReactNode,
    description?: React.ReactNode
  ): void => {
    this.showNotification("error", message, false, position, btn, description);
  };

  public showErrorFixed = (message: string, position?: Position): void => {
    this.showNotification("error", message, true, position);
  };

  public showWarning = (message: string, position?: Position): void => {
    this.showNotification("warning", message, false, position);
  };

  public showWarningFixed = (message: string, position?: Position): void => {
    this.showNotification("warning", message, true, position);
  };

  public showInfo = (message: string, position?: Position): void => {
    this.showNotification("info", message, false, position);
  };

  public showApiErrors = (
    error: ErrorResponse<CIQError> | CompleteValidatorErrors | ApiError,
    position?: Position
  ): void => {
    const apiError = error instanceof ApiError ? error : new ApiError(error);
    apiError.errorMessages.forEach((message) =>
      this.showError(message, position)
    );
  };

  public showAction = (
    message: string,
    action: {
      label: string;
      onClick: () => void;
    },
    position?: Position
  ): void => {
    notification.destroy();
    notification.open({
      message,
      description: `Click to ${action.label}`,
      duration: DEFAULT_DURATION,
      placement: position ? positionMap[position] : DEFAULT_PLACEMENT,
      onClick: () => {
        notification.destroy();
        action.onClick();
      }
    });
  };
}

export const DefaultToasterService = new ToasterService();
